import React, { useState } from 'react'
import {
  Button,
  Popover,
  Select,
  message,
} from '@surecompanies/core_components'
import { Pencil } from 'lucide-react'

import { useSetPullDate } from '../../api/reconciliation-v2-api-client'
import { extractApiErrorMessage } from './AdjustPaymentModal.helpers'
import {
  formatPullDay,
  PULL_DAY_NOT_SET_LABEL,
  PULL_DAY_OPTIONS,
} from './recon-v2-helpers'
import { colors, radius, space } from './tokens'
import { useCanWriteReconciliation } from './useCanWriteReconciliation'

/**
 * A benefit's autopay pull date as recon v2 shows it — in the Benefits table's
 * "Pull date" column, where finance can also edit it. The pull
 * date is the day of month the carrier is expected to pull the premium.
 */

/** "28th", or a muted "Not set". `== null` so an older server that omits the field reads as not set. */
export const PullDayValue: React.FC<{ pullDay: number | null | undefined }> = ({
  pullDay,
}) =>
  pullDay == null ? (
    <span style={{ color: colors.text.disabled }}>
      {PULL_DAY_NOT_SET_LABEL}
    </span>
  ) : (
    <span style={{ whiteSpace: 'nowrap' }}>{formatPullDay(pullDay)}</span>
  )

/**
 * Pencil button + popover picker that sets the benefit's pull day (1–31).
 * Saves through `POST reconciliation-v2/pull-date`, which takes effect
 * immediately — there is deliberately no "apply next month" choice. The
 * mutation invalidates every recon-v2 query, so the list and an open period
 * detail refresh on their own. Callers render it only for operators who may
 * write.
 */
const PullDayEditor: React.FC<{
  benefitId: string
  pullDay: number | null | undefined
}> = ({ benefitId, pullDay }) => {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<number | undefined>(undefined)
  const setPullDate = useSetPullDate()

  const current = pullDay ?? undefined
  const canSave = draft != null && draft !== current && !setPullDate.isPending

  const onOpenChange = (next: boolean) => {
    if (next) setDraft(current)
    setOpen(next)
  }

  const save = () => {
    if (draft == null) return
    setPullDate.mutate(
      { benefitId, pullDay: draft },
      {
        onSuccess: () => {
          message.success(`Pull date set to the ${formatPullDay(draft)}`)
          setOpen(false)
        },
        onError: (err) =>
          message.error(
            `Failed to update pull date: ${extractApiErrorMessage(err)}`
          ),
      }
    )
  }

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
      <span style={{ color: colors.text.muted, fontSize: 12 }}>
        Day of month the carrier pulls. Takes effect immediately.
      </span>
      <Select
        aria-label="Pull day"
        placeholder="Pick a day"
        value={draft}
        onChange={setDraft}
        options={PULL_DAY_OPTIONS}
        // 31 short rows — no need for virtualization, and every day stays
        // reachable in the DOM.
        virtual={false}
        // Render the dropdown inside the popover: on document.body, picking a
        // day reads as a click outside and closes the popover before Save.
        getPopupContainer={(trigger: HTMLElement) =>
          trigger.parentElement ?? document.body
        }
        style={{ width: 160 }}
      />
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', gap: space.xs }}
      >
        <Button size="small" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          disabled={!canSave}
          loading={setPullDate.isPending}
          onClick={save}
        >
          Save
        </Button>
      </div>
    </div>
  )

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      title="Pull date"
      open={open}
      onOpenChange={onOpenChange}
      content={content}
      destroyTooltipOnHide
    >
      <button
        type="button"
        aria-label="Edit pull date"
        title="Edit pull date"
        style={{
          display: 'inline-grid',
          placeItems: 'center',
          width: 20,
          height: 20,
          padding: 0,
          border: 'none',
          borderRadius: radius.sm,
          background: 'transparent',
          color: colors.text.placeholder,
          cursor: 'pointer',
        }}
      >
        <Pencil size={12} aria-hidden />
      </button>
    </Popover>
  )
}

/** Benefits table cell: the pull day, and the edit control for operators who may write. */
export const PullDateCell: React.FC<{
  benefitId: string
  pullDay: number | null | undefined
}> = ({ benefitId, pullDay }) => {
  const { allowed, isLoading } = useCanWriteReconciliation()
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs }}
    >
      <PullDayValue pullDay={pullDay} />
      {allowed && !isLoading ? (
        <PullDayEditor benefitId={benefitId} pullDay={pullDay} />
      ) : null}
    </span>
  )
}
